import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";
import { PaginatedResponse } from "@/lib/types";

// Get all students enrolled in a specific group with proper backend pagination
export async function fetchGroupStudents(
  groupId: string,
  pageNumber = 1,
  pageSize = 10,
  search = "",
  status = "",
): Promise<PaginatedResponse<any>> {
  if (!API_BASE_URL) {
    return {
      items: [],
      pageNumber,
      pageSize,
      totalCount: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }

  const queryParams = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
  });
  if (search) {
    queryParams.append("search", search);
  }
  if (status) {
    queryParams.append("status", status);
  }

  const res = await fetch(
    `${API_BASE_URL}/Students/by-group/${groupId}?${queryParams.toString()}`,
    {
      headers: getAuthHeaders(),
    },
  );
  if (!res.ok) throw new Error("Failed to fetch group students");

  const data = await res.json();
  return data;
}

// Download the CSV template for importing students
export async function downloadStudentsCsvTemplate(): Promise<void> {
  if (!API_BASE_URL) return;
  const res = await fetch(`${API_BASE_URL}/Students/csv-template`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to download CSV template");

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Students_Import_Template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export interface ImportStudentsResponse {
  totalRows: number;
  successCount: number;
  /** Existing students added to the target group (a student may be in several groups). */
  linkedExistingCount?: number;
  /** Rows whose student was already a member of the target group. */
  alreadyInGroupCount?: number;
  failureCount: number;
  /**
   * `column` and `value` come from CsvRowError and were simply not declared here before, so the
   * error report had less to work with than the API actually sends.
   */
  errors: {
    row: number;
    email: string;
    column?: string;
    value?: string;
    errorMessage: string;
  }[];
}

// Import students via CSV
export async function importGroupStudentsCsv({
  groupId,
  file,
}: {
  groupId: string;
  file: File;
}): Promise<ImportStudentsResponse> {
  if (!API_BASE_URL) throw new Error("API Base URL not defined");

  const formData = new FormData();
  formData.append("file", file);

  const headers = getAuthHeaders();
  // Ensure we don't accidentally set Content-Type to application/json
  // fetch will automatically set it to multipart/form-data with correct boundary when body is FormData
  delete headers["Content-Type"];

  const res = await fetch(`${API_BASE_URL}/Students/import-csv?groupId=${groupId}`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    throw new Error("Failed to import students CSV");
  }

  return await res.json();
}

/**
 * StudyLevel enum surface. Two endpoints, two wire formats:
 * - `POST /api/Students` (create) binds `studyLevel` as the numeric enum VALUE (0–5).
 * - `PUT /api/Students/{id}` (update) binds `study` as the enum NAME string ("Level1"…"Graduate").
 *
 * `value` = numeric for create, `name` = enum member for update, `label` = operator-facing text.
 */
export const STUDY_LEVELS = [
  { value: 0, name: "Level1", label: "Level 1" },
  { value: 1, name: "Level2", label: "Level 2" },
  { value: 2, name: "Level3", label: "Level 3" },
  { value: 3, name: "Level4", label: "Level 4" },
  { value: 4, name: "Level5", label: "Level 5" },
  { value: 5, name: "Graduate", label: "Graduate" },
] as const;

/** Enum NAME ("Level1") → operator-facing label ("Level 1"); unknown values pass through. */
export function studyLevelLabel(name: string | null | undefined): string {
  if (!name) return "";
  return STUDY_LEVELS.find((l) => l.name === name)?.label ?? name;
}

export interface CreateStudentPayload {
  email: string;
  /** Exactly 14 digits — it also becomes the account's initial password. */
  nationalId: string;
  studentCode?: string | null;
  /** Numeric StudyLevel; see STUDY_LEVELS. */
  studyLevel?: number;
  /** When set, the new account is added to this group and inherits its courses/tracks. */
  groupId?: string | null;
}

/** The StudentDto fields a caller of `createStudent` can rely on; the API returns the full record. */
export interface CreatedStudent {
  id: string;
  email: string;
  fullName: string | null;
  studentCode: string | null;
  nationalId: string | null;
  studyLevel: string;
}

/**
 * Creates one student account. The same endpoint the CSV importer ends up at per row, so a student
 * added this way is identical to an imported one — National ID normalized the same way, and the same
 * group-content provenance granted when `groupId` is supplied.
 */
export async function createStudent(payload: CreateStudentPayload): Promise<CreatedStudent> {
  if (!API_BASE_URL) throw new Error("API Base URL not defined");

  const res = await fetch(`${API_BASE_URL}/Students`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      email: payload.email.trim(),
      nationalId: payload.nationalId.trim(),
      studentCode: payload.studentCode?.trim() || null,
      studyLevel: payload.studyLevel ?? 0,
      groupId: payload.groupId ?? null,
    }),
  });

  if (!res.ok) {
    // The controller answers a bad payload two different ways: a plain string from
    // InvalidOperationException (duplicate email, malformed National ID) and a ModelState object
    // from the DataAnnotations. Both have to reach the operator — "Failed to create student" tells
    // them nothing about which field to fix.
    const text = await res.text();
    let message = text;
    try {
      const parsed = text ? JSON.parse(text) : null;
      if (typeof parsed === "string") {
        message = parsed;
      } else if (parsed?.errors && typeof parsed.errors === "object") {
        message = Object.values(parsed.errors as Record<string, string[]>)
          .flat()
          .join(" ");
      } else {
        message = parsed?.title ?? parsed?.message ?? text;
      }
    } catch {
      // Not JSON — the raw body is already the message.
    }
    throw new Error(message || `Failed to create student (${res.status})`);
  }

  return await res.json();
}

export interface ResetStudentPasswordResult {
  tempPassword: string;
}

/**
 * Generates a new temporary password for a student and forces a change on next login.
 * Mirrors `resetStaffPassword` — the plaintext comes back exactly once and is never
 * retrievable again, so the caller must show it before discarding the response.
 */
export async function resetStudentPassword(id: string): Promise<ResetStudentPasswordResult> {
  if (!API_BASE_URL) throw new Error("API Base URL not defined");

  const res = await fetch(`${API_BASE_URL}/Students/${id}/reset-password`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}

export interface StudentFilterQueryParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  groupId?: string;
  teamLeaderId?: string;
  trainingStatus?: string[];
  isBlacklisted?: boolean;
  isNotResponding?: boolean;
  /** Blacklisted OR not responding. The two flags above are ANDed and can't express "either". */
  needsAttention?: boolean;
  activity?: string[];
  /** StudyLevel enum NAMES ("Level1"…"Graduate"); see STUDY_LEVELS. */
  studyLevel?: string[];
  /** Batch id — a student reaches a batch through their group. */
  batchId?: string;
  /** true = in at least one group, false = in none, undefined = facet off. */
  hasGroup?: boolean;
  /** ExamFilterState enum names: NotTaken | InProgress | Passed | Failed | Abandoned. */
  examState?: string[];
  /** ProgressBucket enum name: NotStarted | Started | Q1..Q4 | NearlyDone | Completed. */
  progressBucket?: string;
  /** Free-form completion bounds in percent, ANDed with progressBucket. */
  progressMin?: number;
  progressMax?: number;
  sortBy?: string;
  sortDir?: string;
}

export interface StudentGridRow {
  id: string;
  fullName: string | null;
  studentCode: string | null;
  nationalId: string | null;
  avatarUrl: string | null;
  email: string;
  phoneNumber: string | null;
  studyLevel: string;
  /** The team leader of the student's group (falls back to the legacy free-text column). */
  teamLead: string | null;
  /** Batch the student belongs to, resolved through their group. */
  batch: string | null;
  groups: { groupId: string; groupName: string; joinedAt: string }[];
  completedModules: number;
  totalModules: number;
  progressPercent: number;
  trainingStatus: string;
  isBlacklisted: boolean;
  isNotResponding: boolean;
  examStatus: string;
  examScore: number | null;
  /** The authoritative pass/fail result from ExamSession.Passed. Null when no session exists. */
  examPassed: boolean | null;
  activity: string;
  lastSeenAt: string | null;
}

export interface StudentStatusCounts {
  total: number;
  trainingStatus: Record<string, number>;
  blacklisted: number;
  notResponding: number;
  online: number;
  offline: number;
  neverActive: number;
}

export async function fetchStudentGrid(
  params: StudentFilterQueryParams,
): Promise<PaginatedResponse<StudentGridRow>> {
  if (!API_BASE_URL) {
    return {
      items: [],
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      totalCount: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }

  const query = new URLSearchParams();
  if (params.pageNumber !== undefined) query.append("pageNumber", params.pageNumber.toString());
  if (params.pageSize !== undefined) query.append("pageSize", params.pageSize.toString());
  if (params.search) query.append("search", params.search);
  if (params.groupId && params.groupId !== "all" && params.groupId !== "Unassigned") {
    query.append("groupId", params.groupId);
  }
  if (params.teamLeaderId && params.teamLeaderId !== "all") {
    query.append("teamLeaderId", params.teamLeaderId);
  }
  if (params.isBlacklisted !== undefined && params.isBlacklisted !== null) {
    query.append("isBlacklisted", params.isBlacklisted.toString());
  }
  if (params.isNotResponding !== undefined && params.isNotResponding !== null) {
    query.append("isNotResponding", params.isNotResponding.toString());
  }
  // Blacklisted OR not responding, as one facet — `isBlacklisted`/`isNotResponding` are ANDed
  // server-side, so they cannot express "either".
  if (params.needsAttention !== undefined && params.needsAttention !== null) {
    query.append("needsAttention", params.needsAttention.toString());
  }
  if (params.sortBy) query.append("sortBy", params.sortBy);
  if (params.sortDir) query.append("sortDir", params.sortDir);

  if (params.trainingStatus && params.trainingStatus.length > 0) {
    params.trainingStatus.forEach((status) => {
      query.append("trainingStatus", status);
    });
  }

  if (params.activity && params.activity.length > 0) {
    params.activity.forEach((act) => {
      query.append("activity", act);
    });
  }

  if (params.studyLevel && params.studyLevel.length > 0) {
    params.studyLevel.forEach((level) => {
      query.append("studyLevel", level);
    });
  }

  if (params.examState && params.examState.length > 0) {
    params.examState.forEach((state) => {
      query.append("examState", state);
    });
  }

  if (params.batchId && params.batchId !== "all") {
    query.append("batchId", params.batchId);
  }
  if (params.hasGroup !== undefined && params.hasGroup !== null) {
    query.append("hasGroup", params.hasGroup.toString());
  }
  if (params.progressBucket) {
    query.append("progressBucket", params.progressBucket);
  }
  // 0 is a meaningful bound, so these test for undefined rather than falsiness.
  if (params.progressMin !== undefined && params.progressMin !== null) {
    query.append("progressMin", params.progressMin.toString());
  }
  if (params.progressMax !== undefined && params.progressMax !== null) {
    query.append("progressMax", params.progressMax.toString());
  }

  const res = await fetch(`${API_BASE_URL}/Students/grid?${query.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch student grid");
  return await res.json();
}

export async function fetchStudentStatusCounts(): Promise<StudentStatusCounts> {
  if (!API_BASE_URL) {
    return {
      total: 0,
      trainingStatus: {},
      blacklisted: 0,
      notResponding: 0,
      online: 0,
      offline: 0,
      neverActive: 0,
    };
  }

  const res = await fetch(`${API_BASE_URL}/Students/status-counts`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch student status counts");
  return await res.json();
}
